import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Plus, Trash2, Package, Loader2, Barcode, TrendingUp, 
  Calculator, Power, PowerOff, AlertTriangle, Clock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Componente reutilizável de Modal de Produto - IDÊNTICO à página de Produtos
export function ProductModal({ 
  open, 
  onOpenChange, 
  onProductCreated, 
  editingProduct = null,
  categories = [],
  tiposMadeira = []
}) {
  const [loading, setLoading] = useState(false);
  const [localCategories, setLocalCategories] = useState(categories);
  const [localTiposMadeira, setLocalTiposMadeira] = useState(tiposMadeira);
  const [custoHora, setCustoHora] = useState(0);
  
  const [formData, setFormData] = useState({
    nome: '',
    codigo: '',
    descricao: '',
    categoria: '',
    preco_base: '',
    custo: '',
    estoque: '0',
    estoque_minimo: '0',
    observacoes: '',
    ncm: '94036000',
    volume_m3: '',
    tipo_madeira_id: '',
    pecas_madeira: [],
    mao_de_obra: '',
    pintura: '',
    insumos: '',
    tempo_producao_horas: '',
    ativo: true,
    margem_desejada: '30',
    unidade: 'UN',
    nao_usa_madeira: false
  });

  // Estado para nova peça sendo adicionada
  const [novaPeca, setNovaPeca] = useState({
    comprimento: '',
    largura: '',
    espessura: '',
    quantidade: '1'
  });

  // Ref para o campo de comprimento
  const comprimentoRef = useRef(null);

  // Carregar dados auxiliares se não foram passados
  useEffect(() => {
    const fetchAuxData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        
        if (localCategories.length === 0) {
          const catRes = await axios.get(`${API}/settings/categories/product`, { headers });
          setLocalCategories(catRes.data.categories || []);
        }
        
        if (localTiposMadeira.length === 0) {
          const madRes = await axios.get(`${API}/tipos-madeira`, { headers });
          setLocalTiposMadeira(madRes.data || []);
        }
        
        const custoRes = await axios.get(`${API}/custos/mao-de-obra`, { headers }).catch(() => ({ data: { custo_hora: 0 } }));
        setCustoHora(custoRes.data?.custo_hora || 0);
      } catch (error) {
        console.error('Erro ao carregar dados auxiliares:', error);
      }
    };
    
    if (open) {
      fetchAuxData();
    }
  }, [open, localCategories.length, localTiposMadeira.length]);

  // Preencher form se estiver editando
  useEffect(() => {
    if (editingProduct) {
      setFormData({
        nome: editingProduct.nome || '',
        codigo: editingProduct.codigo || '',
        descricao: editingProduct.descricao || '',
        categoria: editingProduct.categoria || '',
        preco_base: editingProduct.preco_base?.toString() || '',
        custo: editingProduct.custo?.toString() || '',
        estoque: editingProduct.estoque?.toString() || '0',
        estoque_minimo: editingProduct.estoque_minimo?.toString() || '0',
        observacoes: editingProduct.observacoes || '',
        ncm: editingProduct.ncm || '94036000',
        volume_m3: editingProduct.volume_m3?.toString() || '',
        tipo_madeira_id: editingProduct.tipo_madeira_id || '',
        pecas_madeira: editingProduct.pecas_madeira || [],
        mao_de_obra: editingProduct.mao_de_obra?.toString() || '',
        pintura: editingProduct.pintura?.toString() || '',
        insumos: editingProduct.insumos?.toString() || '',
        tempo_producao_horas: editingProduct.tempo_producao_horas?.toString() || '',
        ativo: editingProduct.ativo !== false,
        margem_desejada: editingProduct.margem_desejada?.toString() || '30',
        unidade: editingProduct.unidade || 'UN',
        nao_usa_madeira: editingProduct.nao_usa_madeira || false
      });
    } else {
      // Reset form
      setFormData({
        nome: '',
        codigo: '',
        descricao: '',
        categoria: '',
        preco_base: '',
        custo: '',
        estoque: '0',
        estoque_minimo: '0',
        observacoes: '',
        ncm: '94036000',
        volume_m3: '',
        tipo_madeira_id: '',
        pecas_madeira: [],
        mao_de_obra: '',
        pintura: '',
        insumos: '',
        tempo_producao_horas: '',
        ativo: true,
        margem_desejada: '30',
        unidade: 'UN',
        nao_usa_madeira: false
      });
      setNovaPeca({ comprimento: '', largura: '', espessura: '', quantidade: '1' });
    }
  }, [editingProduct, open]);

  // Calcular volume de uma peça
  const calcularVolumePeca = (peca) => {
    const comp = parseFloat(peca.comprimento) || 0;
    const larg = parseFloat(peca.largura) || 0;
    const esp = parseFloat(peca.espessura) || 0;
    const qtd = parseInt(peca.quantidade) || 1;
    // cm³ para m³
    return ((comp * larg * esp) / 1000000) * qtd;
  };

  // Calcular volume total
  const calcularVolumeTotal = (pecas) => {
    return pecas.reduce((total, peca) => total + calcularVolumePeca(peca), 0);
  };

  // Calcular custo da madeira
  const calcularCustoMadeira = useCallback((pecas, tipoId) => {
    const tipo = localTiposMadeira.find(t => t.id === tipoId);
    if (!tipo || !pecas || pecas.length === 0) return 0;
    const volumeTotal = calcularVolumeTotal(pecas);
    return volumeTotal * (tipo.preco_m3 || tipo.custo_m3 || 0);
  }, [localTiposMadeira]);

  // Calcular preço sugerido
  const calcularPrecoSugerido = useCallback((custo, margem) => {
    const custoNum = parseFloat(custo) || 0;
    const margemNum = parseFloat(margem) || 30;
    if (custoNum <= 0) return 0;
    return custoNum / (1 - margemNum / 100);
  }, []);

  // Calcular margem real
  const calcularMargemReal = useCallback((preco, custo) => {
    const precoNum = parseFloat(preco) || 0;
    const custoNum = parseFloat(custo) || 0;
    if (precoNum <= 0) return 0;
    return ((precoNum - custoNum) / precoNum) * 100;
  }, []);

  // Atualizar custo total
  const atualizarCustoTotal = useCallback((novosDados = {}) => {
    const dados = { ...formData, ...novosDados };
    const custoMadeira = calcularCustoMadeira(dados.pecas_madeira, dados.tipo_madeira_id);
    const maoDeObraManual = parseFloat(dados.mao_de_obra) || 0;
    const tempoHoras = parseFloat(dados.tempo_producao_horas) || 0;
    const maoDeObraCalculada = tempoHoras * custoHora;
    const maoDeObra = maoDeObraManual > 0 ? maoDeObraManual : maoDeObraCalculada;
    const pintura = parseFloat(dados.pintura) || 0;
    const insumos = parseFloat(dados.insumos) || 0;
    return (custoMadeira + maoDeObra + pintura + insumos).toFixed(2);
  }, [formData, calcularCustoMadeira, custoHora]);

  // Adicionar peça de madeira
  const handleAddPeca = () => {
    if (!novaPeca.comprimento || !novaPeca.largura || !novaPeca.espessura) {
      toast.error('Preencha comprimento, largura e espessura');
      return;
    }
    const novaPecaComId = {
      ...novaPeca,
      id: Date.now().toString()
    };
    const novasPecas = [...formData.pecas_madeira, novaPecaComId];
    const volumeTotal = calcularVolumeTotal(novasPecas);
    const custoTotal = atualizarCustoTotal({ pecas_madeira: novasPecas });
    setFormData({
      ...formData,
      pecas_madeira: novasPecas,
      volume_m3: volumeTotal.toFixed(6),
      custo: custoTotal
    });
    setNovaPeca({ comprimento: '', largura: '', espessura: '', quantidade: '1' });
    toast.success('Peça adicionada!');
    
    // Focar no campo de comprimento após adicionar peça
    setTimeout(() => {
      comprimentoRef.current?.focus();
    }, 100);
  };

  // Remover peça
  const handleRemovePeca = (pecaId) => {
    const novasPecas = formData.pecas_madeira.filter(p => p.id !== pecaId);
    const volumeTotal = calcularVolumeTotal(novasPecas);
    const custoTotal = atualizarCustoTotal({ pecas_madeira: novasPecas });
    setFormData({
      ...formData,
      pecas_madeira: novasPecas,
      volume_m3: volumeTotal.toFixed(6),
      custo: custoTotal
    });
  };

  // Salvar produto
  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData.nome.trim()) {
      toast.error('Nome do produto é obrigatório');
      return;
    }
    if (!formData.preco_base || parseFloat(formData.preco_base) <= 0) {
      toast.error('Preço de venda é obrigatório');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const productData = {
        ...formData,
        preco_base: parseFloat(formData.preco_base) || 0,
        custo: parseFloat(formData.custo) || 0,
        estoque: parseInt(formData.estoque) || 0,
        estoque_minimo: parseInt(formData.estoque_minimo) || 0,
        volume_m3: parseFloat(formData.volume_m3) || null,
        mao_de_obra: parseFloat(formData.mao_de_obra) || null,
        pintura: parseFloat(formData.pintura) || null,
        insumos: parseFloat(formData.insumos) || null,
        tempo_producao_horas: parseFloat(formData.tempo_producao_horas) || null,
        margem_desejada: parseFloat(formData.margem_desejada) || 30,
        tipo_madeira_id: formData.tipo_madeira_id || null
      };

      let response;
      if (editingProduct) {
        response = await axios.put(`${API}/products/${editingProduct.id}`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Produto atualizado com sucesso!');
      } else {
        response = await axios.post(`${API}/products`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Produto cadastrado com sucesso!');
      }

      if (onProductCreated) {
        onProductCreated(response.data);
      }
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar produto');
    } finally {
      setLoading(false);
    }
  };

  const precoAbaixoCusto = parseFloat(formData.preco_base) < parseFloat(formData.custo) && parseFloat(formData.custo) > 0;
  const margemReal = calcularMargemReal(formData.preco_base, formData.custo);
  const tipoMadeiraSelecionado = localTiposMadeira.find(t => t.id === formData.tipo_madeira_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto overflow-x-hidden pb-6 mb-6 sm:mb-0 rounded-2xl sm:rounded-lg">
        <DialogHeader>
          <DialogTitle className="font-manrope">
            {editingProduct ? 'Editar Produto' : 'Novo Produto'}
          </DialogTitle>
          <DialogDescription>
            {editingProduct ? 'Atualize as informações do produto' : 'Adicione um novo produto ao catálogo'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 1. Nome e Código */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="nome">Nome do Produto</Label>
              <Input
                id="nome"
                placeholder="Ex: Porta de Madeira Maciça"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="codigo" className="flex items-center gap-1">
                <Barcode className="h-3 w-3" />
                SKU / Código
              </Label>
              <Input
                id="codigo"
                placeholder="SKU-001"
                value={formData.codigo}
                onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
              />
            </div>
          </div>

          {/* 2. Status Ativo/Inativo */}
          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
            <Checkbox
              id="ativo"
              checked={!formData.ativo}
              onCheckedChange={(checked) => setFormData({ ...formData, ativo: !checked })}
            />
            <Label htmlFor="ativo" className="flex items-center gap-2 cursor-pointer">
              {!formData.ativo ? (
                <>
                  <PowerOff className="h-4 w-4 text-red-600" />
                  <span className="text-red-600 font-medium">Produto Inativo</span>
                </>
              ) : (
                <>
                  <Power className="h-4 w-4 text-emerald-600" />
                  <span className="text-emerald-600 font-medium">Produto Ativo</span>
                </>
              )}
            </Label>
          </div>

          {/* 3. Descrição */}
          <div className="space-y-2">
            <Label htmlFor="descricao">Descrição</Label>
            <Textarea
              id="descricao"
              placeholder="Descrição detalhada do produto"
              value={formData.descricao}
              onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              rows={3}
            />
          </div>

          {/* 4. Categoria */}
          <div className="space-y-2">
            <Label htmlFor="categoria">Categoria</Label>
            <Select 
              value={formData.categoria || 'none'} 
              onValueChange={(v) => setFormData({ ...formData, categoria: v === 'none' ? '' : v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sem categoria</SelectItem>
                {localCategories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 5. Unidade de Medida */}
          <div className="space-y-2">
            <Label htmlFor="unidade">Unidade de Medida</Label>
            <Select 
              value={formData.unidade || 'UN'} 
              onValueChange={(v) => setFormData({ ...formData, unidade: v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="UN">UN (Unidade)</SelectItem>
                <SelectItem value="ML">Metro Linear</SelectItem>
                <SelectItem value="M2">m² (Metro Quadrado)</SelectItem>
                <SelectItem value="M3">m³ (Metro Cúbico)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* 6. Seção de Preços e Margem */}
          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
              <Calculator className="h-4 w-4 text-emerald-600" />
              Precificação e Margem
            </h4>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="custo">Custo Total (R$)</Label>
                <Input
                  id="custo"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.custo}
                  onChange={(e) => setFormData({ ...formData, custo: e.target.value })}
                  className="font-medium"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="margem_desejada">Margem Desejada (%)</Label>
                <Input
                  id="margem_desejada"
                  type="number"
                  step="1"
                  placeholder="30"
                  value={formData.margem_desejada}
                  onChange={(e) => setFormData({ ...formData, margem_desejada: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <TrendingUp className="h-3 w-3 text-blue-600" />
                  Preço Sugerido
                </Label>
                <div className="h-9 flex items-center justify-between px-3 bg-blue-100 rounded text-blue-700 font-medium">
                  <span>R$ {calcularPrecoSugerido(formData.custo, formData.margem_desejada).toFixed(2)}</span>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 text-xs"
                    onClick={() => setFormData({ 
                      ...formData, 
                      preco_base: calcularPrecoSugerido(formData.custo, formData.margem_desejada).toFixed(2) 
                    })}
                  >
                    Usar
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="preco_base">Preço de Venda (R$)</Label>
                <Input
                  id="preco_base"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.preco_base}
                  onChange={(e) => setFormData({ ...formData, preco_base: e.target.value })}
                  required
                  className={`font-medium ${precoAbaixoCusto ? 'border-red-500 bg-red-50' : ''}`}
                />
              </div>
            </div>

            {/* Alerta e Simulador de Margem */}
            {parseFloat(formData.custo) > 0 && parseFloat(formData.preco_base) > 0 && (
              <div className={`mt-3 p-3 rounded-lg ${
                precoAbaixoCusto 
                  ? 'bg-red-100 border border-red-300' 
                  : margemReal < 10
                  ? 'bg-amber-100 border border-amber-300'
                  : 'bg-emerald-100 border border-emerald-300'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {precoAbaixoCusto ? (
                      <>
                        <AlertTriangle className="h-5 w-5 text-red-600" />
                        <span className="font-medium text-red-700">PREÇO ABAIXO DO CUSTO!</span>
                      </>
                    ) : margemReal < 10 ? (
                      <>
                        <AlertTriangle className="h-5 w-5 text-amber-600" />
                        <span className="font-medium text-amber-700">Margem muito baixa</span>
                      </>
                    ) : (
                      <>
                        <TrendingUp className="h-5 w-5 text-emerald-600" />
                        <span className="font-medium text-emerald-700">Margem saudável</span>
                      </>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">Margem Real</div>
                    <div className={`text-lg font-bold ${
                      precoAbaixoCusto ? 'text-red-700' : margemReal < 10 ? 'text-amber-700' : 'text-emerald-700'
                    }`}>
                      {margemReal.toFixed(1)}%
                    </div>
                  </div>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  Lucro por unidade: <strong>R$ {(parseFloat(formData.preco_base || 0) - parseFloat(formData.custo || 0)).toFixed(2)}</strong>
                </div>
              </div>
            )}

            {/* 7. Estoque */}
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div className="space-y-2">
                <Label htmlFor="estoque">Estoque</Label>
                <Input
                  id="estoque"
                  type="number"
                  placeholder="0"
                  value={formData.estoque}
                  onChange={(e) => setFormData({ ...formData, estoque: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="estoque_minimo">Estoque Mínimo</Label>
                <Input
                  id="estoque_minimo"
                  type="number"
                  placeholder="0"
                  value={formData.estoque_minimo || ''}
                  onChange={(e) => setFormData({ ...formData, estoque_minimo: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* 8. NCM (Código Fiscal) */}
          <div className="space-y-2">
            <Label htmlFor="ncm">NCM (Código Fiscal)</Label>
            <div className="flex gap-2">
              <Select 
                value={['44182100', '44181100', '94036000', '4418.99.00', '4418.21.00', '4418.11.00'].includes(formData.ncm?.replace(/\./g, '')) ? formData.ncm : 'custom'} 
                onValueChange={(v) => {
                  if (v !== 'custom') {
                    setFormData({ ...formData, ncm: v });
                  }
                }}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Selecione ou digite" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="44182100">44182100 - Portas, forras e alizares</SelectItem>
                  <SelectItem value="44181100">44181100 - Janelas</SelectItem>
                  <SelectItem value="94036000">94036000 - Móveis de madeira</SelectItem>
                  <SelectItem value="4418.99.00">4418.99.00 - Outras obras de marcenaria</SelectItem>
                  <SelectItem value="custom">Digitar manualmente...</SelectItem>
                </SelectContent>
              </Select>
              <Input
                id="ncm"
                placeholder="Ex: 4418.21.00"
                value={formData.ncm || ''}
                onChange={(e) => setFormData({ ...formData, ncm: e.target.value })}
                className="w-32"
              />
            </div>
            <p className="text-xs text-muted-foreground">Código para emissão de NF-e no Omie</p>
          </div>

          {/* 9. Observações */}
          <div className="space-y-2">
            <Label htmlFor="observacoes">Observações (aparece no PDF do pedido)</Label>
            <Textarea
              id="observacoes"
              placeholder="Observações sobre o produto que aparecerão na impressão do pedido"
              value={formData.observacoes}
              onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
              rows={3}
            />
          </div>

          {/* 10. Controle de Estoque de Madeira */}
          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
              <Package className="h-4 w-4 text-amber-600" />
              Controle de Estoque de Madeira
            </h4>
            
            {/* Checkbox: Não usa madeira */}
            <div className="flex items-center gap-3 mb-4 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
              <input
                type="checkbox"
                id="nao_usa_madeira"
                checked={formData.nao_usa_madeira}
                onChange={(e) => {
                  const checked = e.target.checked;
                  if (checked) {
                    // Se marcou que não usa madeira, limpar campos de madeira
                    setFormData({ 
                      ...formData, 
                      nao_usa_madeira: checked,
                      tipo_madeira_id: '',
                      volume_m3: '',
                      pecas_madeira: []
                    });
                  } else {
                    setFormData({ ...formData, nao_usa_madeira: checked });
                  }
                }}
                className="h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="nao_usa_madeira" className="text-sm font-medium">
                Este produto não utiliza madeira
              </label>
            </div>
            
            {/* Tipo de Madeira - só mostra se usa madeira */}
            {!formData.nao_usa_madeira && (
            <div className="space-y-2 mb-4">
              <Label htmlFor="tipo_madeira_id">Tipo de Madeira</Label>
              <Select 
                value={formData.tipo_madeira_id || 'none'} 
                onValueChange={(v) => {
                  const tipoId = v === 'none' ? '' : v;
                  const novoCusto = atualizarCustoTotal({ tipo_madeira_id: tipoId });
                  const volumeTotal = calcularVolumeTotal(formData.pecas_madeira);
                  setFormData({ 
                    ...formData, 
                    tipo_madeira_id: tipoId,
                    volume_m3: volumeTotal.toFixed(6),
                    custo: novoCusto
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo de madeira..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum (sem controle)</SelectItem>
                  {localTiposMadeira.map(tipo => (
                    <SelectItem key={tipo.id} value={tipo.id}>
                      {tipo.nome} - R$ {(tipo.preco_m3 || tipo.custo_m3 || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/m³
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}

            {/* Volume por Unidade - só mostra se usa madeira */}
            {!formData.nao_usa_madeira && (
            <div className="space-y-2 mb-4">
              <Label htmlFor="volume_m3">Volume por Unidade (m³)</Label>
              <Input
                id="volume_m3"
                type="number"
                step="0.0001"
                placeholder="0.0000"
                value={formData.volume_m3}
                onChange={(e) => setFormData({ ...formData, volume_m3: e.target.value })}
                disabled={formData.pecas_madeira.length > 0}
              />
            </div>
            )}

            {/* Tabela de Peças (tipo Romaneio) - Aparece quando tipo de madeira selecionado */}
            {!formData.nao_usa_madeira && formData.tipo_madeira_id && (
              <div className="space-y-3 p-3 bg-amber-500/5 border border-amber-500/30 rounded-lg">
                <div className="text-sm font-medium text-amber-700">Peças de Madeira</div>
                
                {/* Formulário para adicionar peça */}
                <div className="grid grid-cols-5 gap-2">
                  <div>
                    <Label className="text-xs">Comp. (cm)</Label>
                    <Input
                      ref={comprimentoRef}
                      type="number"
                      step="0.1"
                      placeholder="210"
                      value={novaPeca.comprimento}
                      onChange={(e) => setNovaPeca({ ...novaPeca, comprimento: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPeca())}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Larg. (cm)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="13"
                      value={novaPeca.largura}
                      onChange={(e) => setNovaPeca({ ...novaPeca, largura: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPeca())}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Esp. (cm)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="3.5"
                      value={novaPeca.espessura}
                      onChange={(e) => setNovaPeca({ ...novaPeca, espessura: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPeca())}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Qtd</Label>
                    <Input
                      type="number"
                      min="1"
                      placeholder="1"
                      value={novaPeca.quantidade}
                      onChange={(e) => setNovaPeca({ ...novaPeca, quantidade: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPeca())}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button type="button" size="sm" onClick={handleAddPeca} className="w-full">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Lista de peças adicionadas */}
                {formData.pecas_madeira.length > 0 && (
                  <div className="space-y-1 mt-2">
                    {formData.pecas_madeira.map((peca, idx) => (
                      <div key={peca.id || idx} className="flex items-center justify-between text-xs bg-background p-2 rounded border">
                        <span>
                          <strong>{peca.quantidade}x</strong> {peca.comprimento}cm × {peca.largura}cm × {peca.espessura}cm
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-amber-600 font-medium">
                            {calcularVolumePeca(peca).toFixed(6)} m³
                          </span>
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 text-destructive"
                            onClick={() => handleRemovePeca(peca.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Subtotal de volume e custo */}
                {formData.pecas_madeira.length > 0 && tipoMadeiraSelecionado && (
                  <div className="text-xs text-right border-t pt-2 mt-2 space-y-1">
                    <div>
                      Volume total: <strong className="text-amber-700">{calcularVolumeTotal(formData.pecas_madeira).toFixed(6)} m³</strong>
                    </div>
                    <div>
                      Custo madeira: <strong className="text-amber-700">
                        R$ {calcularCustoMadeira(formData.pecas_madeira, formData.tipo_madeira_id).toFixed(2)}
                      </strong>
                      <span className="text-muted-foreground ml-1">
                        ({tipoMadeiraSelecionado.nome} @ R$ {(tipoMadeiraSelecionado.preco_m3 || tipoMadeiraSelecionado.custo_m3 || 0).toFixed(2)}/m³)
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tempo de Produção, Mão de Obra, Pintura e Insumos */}
            {formData.tipo_madeira_id && (
              <div className="space-y-4 mt-4">
                {/* Tempo de produção e cálculo automático */}
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="h-4 w-4 text-blue-600" />
                    <Label className="text-sm font-medium text-blue-700">Tempo de Produção</Label>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs">Horas</Label>
                      <Input
                        type="number"
                        step="0.5"
                        placeholder="Ex: 8"
                        value={formData.tempo_producao_horas}
                        onChange={(e) => {
                          const novoCusto = atualizarCustoTotal({ tempo_producao_horas: e.target.value });
                          setFormData({ ...formData, tempo_producao_horas: e.target.value, custo: novoCusto });
                        }}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Custo Calculado (R$/h: {custoHora.toFixed(2)})</Label>
                      <div className="h-9 flex items-center px-3 bg-blue-100 rounded text-blue-700 font-medium">
                        R$ {(parseFloat(formData.tempo_producao_horas || 0) * custoHora).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mão de obra manual, Pintura e Insumos em grid alinhado */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Mão de Obra Manual (R$)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Opcional"
                      value={formData.mao_de_obra}
                      onChange={(e) => {
                        const novoCusto = atualizarCustoTotal({ mao_de_obra: e.target.value });
                        setFormData({ ...formData, mao_de_obra: e.target.value, custo: novoCusto });
                      }}
                    />
                    <p className="text-xs text-muted-foreground">Sobrescreve o calculado</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Pintura (R$)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.pintura}
                      onChange={(e) => {
                        const novoCusto = atualizarCustoTotal({ pintura: e.target.value });
                        setFormData({ ...formData, pintura: e.target.value, custo: novoCusto });
                      }}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Insumos (R$)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Ferragens, cola..."
                      value={formData.insumos}
                      onChange={(e) => {
                        const novoCusto = atualizarCustoTotal({ insumos: e.target.value });
                        setFormData({ ...formData, insumos: e.target.value, custo: novoCusto });
                      }}
                    />
                  </div>
                </div>

                {/* Resumo Verde do Custo Total */}
                {(formData.pecas_madeira.length > 0 || parseFloat(formData.tempo_producao_horas) > 0 || parseFloat(formData.mao_de_obra) > 0 || parseFloat(formData.pintura) > 0 || parseFloat(formData.insumos) > 0) && (
                  <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg space-y-1">
                    {/* Madeira */}
                    {formData.pecas_madeira.length > 0 && tipoMadeiraSelecionado && (
                      <div className="flex justify-between text-sm">
                        <span>Madeira ({calcularVolumeTotal(formData.pecas_madeira).toFixed(4)} m³):</span>
                        <span className="font-medium">R$ {calcularCustoMadeira(formData.pecas_madeira, formData.tipo_madeira_id).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    {/* Mão de Obra - calculada ou manual */}
                    {(parseFloat(formData.tempo_producao_horas) > 0 || parseFloat(formData.mao_de_obra) > 0) && (
                      <div className="flex justify-between text-sm">
                        <span>Mão de Obra{parseFloat(formData.mao_de_obra) > 0 ? ' (manual)' : ` (${formData.tempo_producao_horas}h)`}:</span>
                        <span className="font-medium">
                          R$ {(parseFloat(formData.mao_de_obra) > 0 
                            ? parseFloat(formData.mao_de_obra) 
                            : parseFloat(formData.tempo_producao_horas || 0) * custoHora
                          ).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                    {/* Pintura */}
                    {parseFloat(formData.pintura) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span>Pintura:</span>
                        <span className="font-medium">R$ {parseFloat(formData.pintura).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    {/* Insumos */}
                    {parseFloat(formData.insumos) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span>Insumos:</span>
                        <span className="font-medium">R$ {parseFloat(formData.insumos).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    {/* Total */}
                    <div className="flex justify-between text-sm font-bold text-emerald-700 border-t border-emerald-500/30 pt-2 mt-2">
                      <span>CUSTO TOTAL:</span>
                      <span>R$ {(() => {
                        const custoMadeira = calcularCustoMadeira(formData.pecas_madeira, formData.tipo_madeira_id);
                        const maoDeObraManual = parseFloat(formData.mao_de_obra) || 0;
                        const tempoHoras = parseFloat(formData.tempo_producao_horas) || 0;
                        const maoDeObraCalculada = tempoHoras * custoHora;
                        const maoDeObra = maoDeObraManual > 0 ? maoDeObraManual : maoDeObraCalculada;
                        const pintura = parseFloat(formData.pintura) || 0;
                        const insumos = parseFloat(formData.insumos) || 0;
                        return (custoMadeira + maoDeObra + pintura + insumos).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
                      })()}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 11. Botões */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingProduct ? 'Salvar Alterações' : 'Criar Produto'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
